import axios from 'axios';
import { buildApiUrl } from '../../services/apiConfig';

// Sample training scenarios data
const MOCK_SCENARIOS = [
  {
    id: 'scenario-1',
    title: 'Late Payment Due to Job Loss',
    description: 'Member is 45 days past due on their auto loan payment after losing their job. They want to avoid repossession but are struggling financially.',
    difficulty: 'medium',
    initialPrompt: "Hello, this is John. I received a notice about my car payment being late. I lost my job last month and I'm worried about my car being repossessed.",
    keyPoints: [
      'Empathize with job loss situation',
      'Explain repossession timeline and consequences',
      'Explore hardship program options',
      'Discuss potential payment plans'
    ],
    memberProfile: {
      name: 'John Davis',
      age: 34,
      accountHistory: 'Member for 5 years, previously good payment history',
      financialSituation: 'Recently unemployed, receiving unemployment benefits, has some savings'
    }
  },
  {
    id: 'scenario-2',
    title: 'Natural Disaster Relief Request',
    description: 'Member affected by recent hurricane is requesting payment relief on multiple accounts. They need immediate assistance as their home was damaged.',
    difficulty: 'easy',
    initialPrompt: "Hi, my name is Sarah. My home was damaged in the recent hurricane and I won't be able to make payments on my credit card and personal loan this month. Is there any disaster relief available?",
    keyPoints: [
      'Acknowledge disaster situation with empathy',
      'Explain disaster relief programs',
      'Process immediate relief options',
      'Document situation properly'
    ],
    memberProfile: {
      name: 'Sarah Johnson',
      age: 42,
      accountHistory: 'Member for 10 years, excellent payment history',
      financialSituation: 'Temporarily displaced, insurance claim pending, stable employment'
    }
  },
  {
    id: 'scenario-3',
    title: 'Aggressive Member with Multiple Missed Payments',
    description: 'Member is 90+ days delinquent on multiple accounts and is becoming confrontational. They claim they never received previous notices.',
    difficulty: 'hard',
    initialPrompt: "This is Michael. I just got your letter threatening to send my account to collections, and I'm extremely upset. I never received any previous notices about missed payments. This is completely unacceptable!",
    keyPoints: [
      'De-escalate the confrontational situation',
      'Verify contact information',
      'Explain account status clearly',
      'Present immediate options to resolve delinquency'
    ],
    memberProfile: {
      name: 'Michael Wilson',
      age: 38,
      accountHistory: 'Member for 3 years, increasing pattern of late payments',
      financialSituation: 'Employed but overextended on multiple debts'
    }
  }
];

// Mock feedback templates
const FEEDBACK_TEMPLATES = {
  positive: [
    'Demonstrated excellent empathy when addressing the member\'s concerns',
    'Clearly explained available options in easy-to-understand terms',
    'Maintained professional tone throughout the conversation',
    'Asked appropriate questions to understand the member\'s situation',
    'Provided specific, actionable next steps for the member'
  ],
  negative: [
    'Could have shown more empathy regarding the member\'s situation',
    'Explanation of policies was overly technical and difficult to understand',
    'Missed opportunity to explore all available assistance options',
    'Didnt fully address the member\'s primary concerns',
    'Could have provided more specific guidance on next steps'
  ],
  suggestions: [
    'Try acknowledging emotions before moving to solutions',
    'Consider using simpler language when explaining complex policies',
    'Ask more open-ended questions to better understand the situation',
    'Offer specific timeframes when discussing next steps',
    'Summarize the agreed actions at the end of the conversation',
    'Proactively mention relevant assistance programs based on the situation'
  ]
};

const trainingSimulationService = {
  // Get available training scenarios
  getScenarios: async () => {
    // Mock implementation - in a real app, this would call an API
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(MOCK_SCENARIOS);
      }, 800); // Simulate network delay
    });
  },

  // Get specific scenario details
  getScenarioById: async (scenarioId) => {
    // Mock implementation - in a real app, this would call an API
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const scenario = MOCK_SCENARIOS.find(s => s.id === scenarioId);
        if (scenario) {
          resolve(scenario);
        } else {
          reject(new Error(`Scenario with ID ${scenarioId} not found`));
        }
      }, 500); // Simulate network delay
    });
  },

  // Generate avatar response based on counselor input and scenario context
  generateResponse: async (scenarioId, counselorInput, conversationHistory) => {
    try {
      // Create a text file from the conversation history to analyze with Jamba API
      const scenario = MOCK_SCENARIOS.find(s => s.id === scenarioId);
      if (!scenario) {
        throw new Error(`Scenario with ID ${scenarioId} not found`);
      }

      // Format conversation history as a text file
      let conversationText = `SCENARIO: ${scenario.title}\n\n`;
      conversationText += `MEMBER PROFILE: ${JSON.stringify(scenario.memberProfile)}\n\n`;
      conversationText += `CONVERSATION:\n`;
      
      conversationHistory.forEach(entry => {
        conversationText += `${entry.role.toUpperCase()}: ${entry.content}\n\n`;
      });

      // Create prompt for Jamba API
      const prompt = `You are roleplaying as a member named ${scenario.memberProfile.name} who is contacting Navy Federal Credit Union about a collections issue. 
      The scenario is: ${scenario.description}
      
      Based on the conversation history provided, generate a realistic response from the member. 
      The response should be conversational, reflect the member's emotional state, and be appropriate for the scenario context.
      
      If this is early in the conversation, the member should express their concerns and issues.
      If the counselor was empathetic and helpful, the member should respond more positively.
      If the counselor was not addressing their concerns, the member should express continued frustration.
      
      ONLY respond as the member would. Do not provide any analysis or commentary.
      Keep the response under 100 words.`;

      // Create form data for Jamba API
      const transcriptionFile = new Blob([conversationText], { type: 'text/plain' });
      const formData = new FormData();
      formData.append('file', transcriptionFile, 'conversation.txt');
      formData.append('prompt', prompt);

      // Call Jamba API
      const response = await axios.post(buildApiUrl('JAMBA', '/custom-analyze'), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Check if this is the end of the scenario (typically after 5-6 exchanges)
      const isEndOfScenario = conversationHistory.length >= 10;

      return {
        content: response.data.data || "I understand your explanation. What options do I have at this point?",
        isEndOfScenario
      };
    } catch (error) {
      console.error('Error generating response:', error);
      // Fallback response if API fails
      return {
        content: "I see. Can you tell me more about what options I have in this situation?",
        isEndOfScenario: false
      };
    }
  },

  // Get feedback on counselor's handling of the scenario
  getFeedback: async (scenarioId, conversationHistory) => {
    try {
      // Create a text file from the conversation history to analyze with Jamba API
      const scenario = MOCK_SCENARIOS.find(s => s.id === scenarioId);
      if (!scenario) {
        throw new Error(`Scenario with ID ${scenarioId} not found`);
      }

      // Format conversation history as a text file
      let conversationText = `SCENARIO: ${scenario.title}\n\n`;
      conversationText += `SCENARIO DESCRIPTION: ${scenario.description}\n\n`;
      conversationText += `KEY POINTS TO ADDRESS: ${scenario.keyPoints.join(', ')}\n\n`;
      conversationText += `CONVERSATION:\n`;
      
      conversationHistory.forEach(entry => {
        conversationText += `${entry.role.toUpperCase()}: ${entry.content}\n\n`;
      });

      // Create prompt for Jamba API
      const prompt = `You are an expert in evaluating customer service interactions for a credit union's collections department.
      
      Analyze the conversation between a collections counselor and a member regarding a delinquent account.
      
      Provide feedback on the counselor's performance with the following structure:
      1. Overall score (1-5, where 5 is excellent)
      2. Three specific strengths demonstrated by the counselor
      3. Three specific areas where the counselor could improve
      4. Three practical suggestions for handling similar situations better in the future
      
      Focus your analysis on empathy, clarity of explanation, solution-oriented approach, and adherence to the key points that needed to be addressed.`;

      // Create form data for Jamba API
      const transcriptionFile = new Blob([conversationText], { type: 'text/plain' });
      const formData = new FormData();
      formData.append('file', transcriptionFile, 'conversation_for_feedback.txt');
      formData.append('prompt', prompt);

      // Call Jamba API
      const response = await axios.post(buildApiUrl('JAMBA', '/custom-analyze'), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Parse the response to extract structured feedback
      // This is a simplified parsing - in a real app, you might want more robust parsing
      const feedbackText = response.data.data || "";
      
      // Extract overall score (looking for a number 1-5)
      const scoreMatch = feedbackText.match(/overall score:?\s*(\d)/i) || 
                         feedbackText.match(/score:?\s*(\d)/i) ||
                         feedbackText.match(/rating:?\s*(\d)/i);
      const overallScore = scoreMatch ? parseInt(scoreMatch[1]) : 3;
      
      // Extract strengths, improvements and suggestions
      // This is a very basic extraction - a real implementation would be more robust
      const strengths = extractListItems(feedbackText, ['strength', 'positive', 'good', 'well'], 3);
      const improvements = extractListItems(feedbackText, ['improve', 'weakness', 'area', 'better'], 3);
      const suggestions = extractListItems(feedbackText, ['suggest', 'recommendation', 'could', 'should'], 3);

      return {
        overallScore,
        strengths: strengths.length > 0 ? strengths : getRandomItems(FEEDBACK_TEMPLATES.positive, 3),
        improvements: improvements.length > 0 ? improvements : getRandomItems(FEEDBACK_TEMPLATES.negative, 3),
        suggestions: suggestions.length > 0 ? suggestions : getRandomItems(FEEDBACK_TEMPLATES.suggestions, 3)
      };
    } catch (error) {
      console.error('Error generating feedback:', error);
      // Fallback feedback if API fails
      return {
        overallScore: 3,
        strengths: getRandomItems(FEEDBACK_TEMPLATES.positive, 3),
        improvements: getRandomItems(FEEDBACK_TEMPLATES.negative, 3),
        suggestions: getRandomItems(FEEDBACK_TEMPLATES.suggestions, 3)
      };
    }
  },

  // Convert text to speech for avatar
  textToSpeech: async (text) => {
    try {
      // In a real implementation, this would call your TTS API
      // For now, we'll create a mock implementation that returns a silent audio blob
      
      // Create a silent audio context
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      gainNode.gain.value = 0; // Silent
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      const duration = text.length * 0.05; // Rough estimate of speech duration
      
      oscillator.start();
      
      return new Promise(resolve => {
        setTimeout(() => {
          oscillator.stop();
          
          // Create a mock audio blob
          const mockAudioBlob = new Blob([new ArrayBuffer(1000)], { type: 'audio/wav' });
          resolve(mockAudioBlob);
        }, 500); // Simulate processing time
      });
    } catch (error) {
      console.error('Error in text-to-speech:', error);
      throw error;
    }
  },

  // Process audio for avatar playback
  processAudio: async (audioBlob) => {
    try {
      // In a real implementation, this would call your audio processing API
      // For now, we'll create a mock implementation
      
      return {
        audio_url: URL.createObjectURL(audioBlob),
        audio_length: 3.5 // Mock duration in seconds
      };
    } catch (error) {
      console.error('Error processing audio:', error);
      throw error;
    }
  }
};

// Helper function to extract list items from text based on keywords
function extractListItems(text, keywords, count) {
  const sentences = text.split(/[.!?]/).filter(s => s.trim().length > 0);
  const matchingSentences = sentences.filter(sentence => 
    keywords.some(keyword => sentence.toLowerCase().includes(keyword.toLowerCase()))
  );
  
  // Clean up the sentences and take only the requested count
  return matchingSentences
    .map(s => s.trim().replace(/^\d+[\.\)\-]?\s*/, '')) // Remove leading numbers/bullets
    .filter(s => s.length > 10) // Filter out very short items
    .slice(0, count);
}

// Helper function to get random items from an array
function getRandomItems(array, count) {
  const shuffled = [...array].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
}

export default trainingSimulationService;